import { useState, useEffect, useMemo } from "react";
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import './DataCompare.css';
import { commonFieldsDefinition, sourceFieldAccess, sources as availableSources } from '../../../lib/fieldMappingConfig.js';

function cleanValue(v) {
    if (v === null || v === undefined || v === false)
        return "";
    if (typeof v === 'object' && v !== null && v.hasOwnProperty('en_US')) {
        return cleanValue(v.en_US);
    }
    return v.toString();
}

function areValuesSame(v1, v2) {
    const val1 = cleanValue(v1);
    const val2 = cleanValue(v2);

    if (typeof val1 === 'string' && typeof val2 === 'string') {
        return val1.toLowerCase() === val2.toLowerCase();
    }
    return val1 === val2;
}

function CategoryRow({ name, colSpan }) {
    return <tr className="category-row"><td colSpan={colSpan}>{name}</td></tr>
}

function SpacingRow({ colSpan }) {
    return <tr className="spacing-row"><td colSpan={colSpan}>&nbsp;</td></tr>
}

function GetValue(obj, path) {
    if (!obj || !path)
        return null;

    // Handle array of paths for fallback logic (e.g., pending price -> list price)
    if (Array.isArray(path)) {
        for (const singlePath of path) {
            const value = GetValue(obj, singlePath);
            if (value !== null && value !== undefined) {
                return value;
            }
        }
        return null;
    }

    let parts = path.split('.');
    if (parts.length > 1 && obj && typeof obj === 'object' && parts[0] in obj)
        return GetValue(obj[parts[0]], parts.slice(1).join('.'));
    else if (obj && typeof obj === 'object' && parts[0] in obj)
        return obj[parts[0]];
    else
        return null;
}

function GetEquivalenceValue(sourceKey, obj, path) {
    const value = GetValue(obj, path);
    if (sourceKey === "VINSOL" && path === "core.inventoryType") {
        if (value === "N") return "NEW";
        if (value === "U") return "USED";
    }
    if (value === 0) 
        return "";

    if (sourceKey === "CVI" && path === "inventoryVehicle.purchaseStatus" && (value === "NONE" || value === "UNSPECIFIED")) {
        return "";
    }
    if (sourceKey === "VAUTO" && path === "status.purchaseStatus" && (value === "NONE" || value === "UNSPECIFIED")) {
        return "";
    }
    return value;
}

function DataCompare(props) {
    const { allAvailableData, sourceOrdering } = props;
    const [masterSourceKey, setMasterSourceKey] = useState('');
    const sourceOrderMap = sourceOrdering.map((sourceKey, index) => ({ sourceKey, index })).reduce((map, obj) => {
        map[obj.sourceKey] = obj.index;
        return map;
    }, {});

    // Determine if any data source is still loading
    const anyLoading = allAvailableData
        ? Object.keys(allAvailableData).some(key =>
            allAvailableData[key] && allAvailableData[key].status === "LOADING"
        )
        : false;

    // Extract just the .data properties for sources that have finished loading with data
    const resolvedData = useMemo(() => {
        if (!allAvailableData) return {};
        return Object.entries(allAvailableData)
            .filter(([key, value]) => value && value.status === "READY" && value.data && Object.keys(value.data).length > 0)
            .reduce((obj, [key, value]) => {
                obj[key] = value.data;
                return obj;
            }, {});
    }, [allAvailableData]);

    useEffect(() => {
        // Only establish masterSourceKey once all sources have finished loading
        if (anyLoading) return;

        const currentAvailableKeys = Object.keys(resolvedData).filter(key => availableSources[key]);

        if (currentAvailableKeys.length > 0) {
            if (!masterSourceKey || !currentAvailableKeys.includes(masterSourceKey)) {
                setMasterSourceKey(sourceOrdering.filter(key => currentAvailableKeys.includes(key))[0] || '');
            }
        } else {
            setMasterSourceKey('');
        }
    }, [resolvedData, masterSourceKey, anyLoading]);

    if (!allAvailableData) {
        return <p>No data available for comparison.</p>;
    }

    if (anyLoading) {
        return <div className="data-compare-container"><Spinner animation="border" /> Loading data sources...</div>;
    }

    const validSourceKeysWithData = Object.keys(resolvedData)
        .filter(key => availableSources[key]);

    let orderedDisplaySourceKeys = [];
    if (masterSourceKey && validSourceKeysWithData.includes(masterSourceKey)) {
        orderedDisplaySourceKeys = [
            masterSourceKey,
            ...validSourceKeysWithData.filter(k => k !== masterSourceKey).sort((a, b) => sourceOrderMap[a] - sourceOrderMap[b])
        ];
    } else if (validSourceKeysWithData.length > 0) {
        const tempMaster = sourceOrdering[0];
        orderedDisplaySourceKeys = [
            tempMaster,
            ...validSourceKeysWithData.filter(k => k !== tempMaster).sort((a, b) => sourceOrderMap[a] - sourceOrderMap[b])
        ];
    }

    const masterVehicleData = masterSourceKey ? resolvedData[masterSourceKey] : null;

    const numColumns = 1 + orderedDisplaySourceKeys.length;

    return (
        <div className="data-compare-container">
            {validSourceKeysWithData.length === 0 && (
                <p>No data found for any source for the current vehicle.</p>
            )}

            {masterSourceKey && masterVehicleData && orderedDisplaySourceKeys.length > 0 ? (
                <div className="data-compare">
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                {orderedDisplaySourceKeys.map(key => (
                                    <th key={key} className={key === masterSourceKey ? 'master-header' : ''}>
                                        <button
                                            onClick={() => setMasterSourceKey(key)}
                                            className="header-button" // Added a class for potential styling
                                            style={{ background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', cursor: 'pointer', width: '100%', textAlign: 'inherit' }} // Basic styling to make it look like part of the header
                                        >
                                            {availableSources[key]}
                                            {key === masterSourceKey ? ' (Compare To)' : ' ←'}
                                        </button>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {commonFieldsDefinition.map((fieldDef, index) => {
                                const keySuffix = fieldDef.displayName ? fieldDef.displayName.replace(/\\s+/g, '-') : index;

                                if (fieldDef.isCategory) {
                                    return <CategoryRow key={`cat-${index}-${keySuffix}`} name={fieldDef.displayName} colSpan={numColumns} />;
                                }
                                if (fieldDef.isSpacing) {
                                    return <SpacingRow key={`space-${index}`} colSpan={numColumns} />;
                                }

                                const masterEquivalenceValue = masterVehicleData && sourceFieldAccess[masterSourceKey]
                                    ? GetEquivalenceValue(masterSourceKey, masterVehicleData, sourceFieldAccess[masterSourceKey][fieldDef.attribute])
                                    : null;
                                const masterRawPath = sourceFieldAccess[masterSourceKey]
                                                ? sourceFieldAccess[masterSourceKey][fieldDef.attribute]
                                                : null;

                                return (
                                    <tr key={`field-${index}-${keySuffix}`}>
                                        <td>{fieldDef.displayName}</td>
                                        {orderedDisplaySourceKeys.map(currentSourceKey => {
                                            const currentVehicleData = resolvedData[currentSourceKey];
                                            const currentRawValue = currentVehicleData && sourceFieldAccess[currentSourceKey]
                                                ? GetValue(currentVehicleData, sourceFieldAccess[currentSourceKey][fieldDef.attribute])
                                                : null;
                                            const currentEquivalenceValue = currentVehicleData && sourceFieldAccess[currentSourceKey]
                                                ? GetEquivalenceValue(currentSourceKey, currentVehicleData, sourceFieldAccess[currentSourceKey][fieldDef.attribute])
                                                : null;

                                            const currentDisplayValue = fieldDef.isDate
                                                ? (currentRawValue ? new Date(currentRawValue).toLocaleDateString() : "")
                                                : cleanValue(currentRawValue);

                                            const rawPath = sourceFieldAccess[currentSourceKey]
                                                ? sourceFieldAccess[currentSourceKey][fieldDef.attribute]
                                                : null;
                                            const rawPathDisplay = Array.isArray(rawPath) ? rawPath.join(' | ') : rawPath;

                                            let cellClass = "";
                                            if (currentSourceKey === masterSourceKey) {
                                                cellClass = "master-cell";
                                            } else {
                                                const currentCleanedValue = cleanValue(currentEquivalenceValue);
                                                const masterCleanedValue = cleanValue(masterEquivalenceValue);
                                                if (!masterRawPath) {
                                                    cellClass = "match";
                                                } else if (currentCleanedValue === "" && masterCleanedValue === "") {
                                                    cellClass = "match";
                                                } else if (masterCleanedValue === "" && currentCleanedValue !== "") {
                                                    cellClass = "missing-master";
                                                } else if (currentCleanedValue === "") {
                                                    cellClass = "match";
                                                } else {
                                                    cellClass = areValuesSame(masterEquivalenceValue, currentEquivalenceValue) ? "match" : "diff";
                                                }
                                            }

                                            return (
                                                <td key={`${currentSourceKey}-${index}-${keySuffix}`} className={cellClass}>
                                                    <span className="cell-value">{currentDisplayValue || (rawPath ? '\u2014' : "")}</span>
                                                    {rawPathDisplay && <span className="cell-raw-path">{rawPathDisplay}</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            ) : (
                validSourceKeysWithData.length > 0 && <p>Select a master source to view comparison. If a source is selected, it might not have data for the current vehicle, or configuration might be loading.</p>
            )}
        </div>
    );
}

export default DataCompare;